"use client";

import Image from "next/image";
import Link from "next/link";
import { TriangleRight } from "@/components/icons/assets";
import { useAppSelector } from "@/store/hooks";
import { useCartDetail } from "@/components/hooks/use-cart-detail";
import { useAddProduct } from "@/components/hooks/use-add-to-cart";
import Price from "@/components/price";
import { NOT_IMAGE } from "@/lib/constants";
import type { CartItem } from "@/lib/bagisto/types";

export default function CartItems() {
  useCartDetail();
  const cartDetail = useAppSelector((state) => state.cartDetail);
  const cart = cartDetail?.cart;
  const { onAddToRemove } = useAddProduct();

  const handleRemoveAll = () => {
    cart?.items?.forEach((item: CartItem) => {
      onAddToRemove(item.id);
    });
  };

  return (
    <section className="px-4 md:px-8 lg:px-20 pt-32 pb-8 md:pb-16">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-8 md:mb-16">
        <Link href="/" className="font-plus-jakarta text-sm text-neutral-300 hover:text-neutral-900">
          Home
        </Link>
        <TriangleRight className="w-2 h-2.5 text-neutral-300" />
        <span className="font-plus-jakarta text-sm font-medium text-neutral-900">
          Add To Cart
        </span>
      </div>

      {/* Header - Full Width */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="font-plus-jakarta text-lg md:text-2xl font-bold text-neutral-900">
          ADD TO CART
        </h1>
        <button 
          onClick={handleRemoveAll}
          className="font-plus-jakarta text-sm text-error hover:underline cursor-pointer"
        >
          Remove All
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 pb-8 md:pb-28 border-b border-stroke">
        {/* Left: Cart Items */}
        <div className="flex-1">

          {/* Cart Items List */}
          <div className="space-y-4">
            {cart?.items?.map((item: CartItem, index: number) => (
              <div
                key={item.id}
                className={`flex flex-col md:flex-row items-start md:items-center justify-between gap-4 py-6 ${cart.items.length > 1 && index < cart.items.length - 1 ? 'border-b border-stroke' : ''}`}
              >
                {/* Left: Image + Info */}
                <div className="flex items-start gap-4 md:gap-8 flex-1">
                  <Link href={`/product-details/${item.product.urlKey}?type=${item.product.type}`}>
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-background-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Image
                        src={item.product.cacheBaseImage?.at(0)?.smallImageUrl || item.product.images?.at(0)?.url || NOT_IMAGE}
                        alt={item.product.name}
                        width={60}
                        height={60}
                        className="object-contain"
                        onError={(e) => (e.currentTarget.src = NOT_IMAGE)}
                      />
                    </div>
                  </Link>
                  <div>
                    <Link href={`/product-details/${item.product.urlKey}?type=${item.product.type}`}>
                      <h3 className="font-plus-jakarta text-sm md:text-base font-medium text-neutral-900 mb-2">
                        {item.product.name}
                      </h3>
                    </Link>
                    <p className="font-plus-jakarta text-sm text-neutral-900 font-medium px-3 py-1 border border-stroke rounded-lg inline-block mb-2">
                      {item.quantity}
                    </p>
                    <button 
                      onClick={() => onAddToRemove(item.id)}
                      className="font-plus-jakarta text-sm text-neutral-300 hover:underline cursor-pointer block"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Right: Price */}
                <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                  <Price
                    amount={item.total}
                    currencyCode={cart?.cartCurrencyCode}
                    className="font-plus-jakarta text-xl font-bold text-neutral-900"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Summary */}
        <div className="w-full lg:w-[500px]">
          <div className="bg-background-50 rounded-xl px-5 py-[30px] flex flex-col gap-[60px]">
            <div className="flex items-center justify-between">
              <span className="font-plus-jakarta text-base text-neutral-300">Sub Total</span>
              <Price
                amount={cart?.subTotal || "0"}
                currencyCode={cart?.cartCurrencyCode || "USD"}
                className="font-plus-jakarta text-xl font-bold text-neutral-900"
              />
            </div>
            <Link
              href="/checkout"
              className="block w-full bg-primary hover:bg-primary/90 text-white text-center py-3 rounded-xl font-plus-jakarta font-medium transition-colors"
            >
              Check Out
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
